import { useCallback, useEffect, useRef, useState } from "react";

const YT_API_SRC = "https://www.youtube.com/iframe_api";
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function extractId(url) {
  const m = url?.match(/(?:youtu\.be\/|v=|\/embed\/)([\w-]{6,})/);
  return m ? m[1] : url;
}

function loadApi() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
    if (!document.querySelector(`script[src="${YT_API_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = YT_API_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
  });
}

function fmt(t) {
  if (!t || Number.isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Locked-down YouTube player: native controls disabled, click-through to YT
 * suppressed, custom control bar with only Play/Pause · Volume · Speed · FS.
 * Progress bar is display-only (no scrubbing) to keep the surface minimal.
 */
export default function YouTubePlayer({ url, onMilestone }) {
  const wrapRef = useRef(null);
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(0);
  const firedRef = useRef(new Set());

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [speed, setSpeed] = useState(1);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [fs, setFs] = useState(false);
  const [showSpeed, setShowSpeed] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef(0);
  const barRef = useRef(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [hoverPct, setHoverPct] = useState(null);
  const wasPlayingBeforeScrub = useRef(false);
  const [errorCode, setErrorCode] = useState(null);
  const playAttemptRef = useRef(0);
  const playTimeoutRef = useRef(0);

  const id = extractId(url);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const YT = await loadApi();
      if (cancelled || !mountRef.current) return;
      playerRef.current = new YT.Player(mountRef.current, {
        videoId: id,
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          playsinline: 1,
          showinfo: 0,
          cc_load_policy: 0,
          autohide: 1,
        },
        events: {
          onReady: (e) => {
            setReady(true);
            setDur(e.target.getDuration());
            e.target.setVolume(volume);
            e.target.setPlaybackRate(1);
          },
          onStateChange: (e) => {
            const s = e.data;
            setPlaying(s === 1);
            if (s === 1) {
              if (!dur) setDur(e.target.getDuration());
              clearTimeout(playTimeoutRef.current);
            }
          },
          onPlaybackRateChange: (e) => setSpeed(e.data),
          onError: (e) => setErrorCode(e.data),
        },
      });
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(pollRef.current);
      try { playerRef.current?.destroy(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll current time + fire milestones
  useEffect(() => {
    if (!ready || !playing) return;
    const tick = () => {
      const p = playerRef.current;
      if (p && typeof p.getCurrentTime === "function") {
        const t = p.getCurrentTime();
        const d = p.getDuration() || dur;
        setCur(t);
        if (d > 0 && onMilestone) {
          const pct = t / d;
          for (const m of [0.25, 0.5, 0.75, 1.0]) {
            const key = String(m);
            if (!firedRef.current.has(key) && pct >= m - 0.005) {
              firedRef.current.add(key);
              onMilestone(m);
            }
          }
        }
      }
      pollRef.current = requestAnimationFrame(tick);
    };
    pollRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(pollRef.current);
  }, [ready, playing, dur, onMilestone]);

  // Fullscreen state sync
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // When we surface a branded error, stop the underlying player so it doesn't
  // keep buffering or briefly flash YouTube's own error UI behind our overlay.
  useEffect(() => {
    if (errorCode === null) return;
    try {
      playerRef.current?.stopVideo?.();
      playerRef.current?.destroy?.();
      playerRef.current = null;
    } catch (e) { /* noop */ }
  }, [errorCode]);

  const togglePlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    if (playing) {
      p.pauseVideo();
      clearTimeout(playTimeoutRef.current);
    } else {
      playAttemptRef.current += 1;
      const attempt = playAttemptRef.current;
      p.playVideo();
      clearTimeout(playTimeoutRef.current);
      // If the player has not actually started playing within 4.5s, assume
      // an embed restriction blocked it and surface a branded fallback.
      playTimeoutRef.current = setTimeout(() => {
        if (attempt !== playAttemptRef.current) return;
        const state = playerRef.current?.getPlayerState?.();
        // 1 = playing, 2 = paused (user paused). Anything else = failed to start.
        if (state !== 1 && state !== 2) setErrorCode(150);
      }, 4500);
    }
  }, [playing]);

  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    if (muted) { p.unMute(); setMuted(false); }
    else { p.mute(); setMuted(true); }
  }, [muted]);

  const changeVolume = useCallback((v) => {
    const p = playerRef.current;
    setVolume(v);
    if (!p) return;
    p.setVolume(v);
    if (v > 0 && muted) { p.unMute(); setMuted(false); }
  }, [muted]);

  const changeSpeed = useCallback((s) => {
    const p = playerRef.current;
    if (p) p.setPlaybackRate(s);
    setSpeed(s);
    setShowSpeed(false);
  }, []);

  const toggleFs = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  const pctFromEvent = useCallback((clientX) => {
    const el = barRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const p = (clientX - rect.left) / rect.width;
    return Math.min(1, Math.max(0, p));
  }, []);

  const seekToPct = useCallback((p) => {
    const player = playerRef.current;
    if (!player || !dur) return;
    const t = p * dur;
    setCur(t);
    // Also drop already-fired milestones that we're skipping backwards past so they can refire
    for (const m of [0.25, 0.5, 0.75, 1.0]) {
      const key = String(m);
      if (p < m - 0.005) firedRef.current.delete(key);
    }
    player.seekTo(t, true);
  }, [dur]);

  const onBarPointerDown = useCallback((e) => {
    e.preventDefault();
    const player = playerRef.current;
    if (!player) return;
    wasPlayingBeforeScrub.current = playing;
    setScrubbing(true);
    const p = pctFromEvent(e.clientX);
    seekToPct(p);
    // Pause during scrub for precision, resume after
    try { player.pauseVideo(); } catch { /* noop */ }

    const onMove = (ev) => {
      const np = pctFromEvent(ev.clientX);
      setHoverPct(np);
      seekToPct(np);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setScrubbing(false);
      if (wasPlayingBeforeScrub.current) {
        try { playerRef.current?.playVideo(); } catch { /* noop */ }
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [playing, pctFromEvent, seekToPct]);

  const onBarHover = useCallback((e) => {
    if (scrubbing) return;
    setHoverPct(pctFromEvent(e.clientX));
  }, [scrubbing, pctFromEvent]);

  const skip = useCallback((seconds) => {
    const player = playerRef.current;
    if (!player || !dur) return;
    const next = Math.min(dur, Math.max(0, cur + seconds));
    seekToPct(next / dur);
  }, [cur, dur, seekToPct]);

  const nudgeControls = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 2600);
  }, [playing]);

  useEffect(() => {
    if (!playing) setShowControls(true);
    else nudgeControls();
  }, [playing, nudgeControls]);

  const pct = dur > 0 ? (cur / dur) * 100 : 0;

  return (
    <div
      ref={wrapRef}
      className="relative h-full w-full bg-black group"
      onMouseMove={nudgeControls}
      onMouseLeave={() => playing && setShowControls(false)}
      data-testid="yt-player-wrap"
    >
      {/*
        The YT iframe is intentionally OVERSIZED so YouTube's default title bar
        (top ~72px) and native control chrome (bottom ~60px + "Watch on YouTube"
        overlay) are cropped outside the visible frame by the parent's
        overflow-hidden. Pointer events are off so nothing leaks to YouTube.
      */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          ref={mountRef}
          className="absolute left-0 right-0"
          style={{ top: "-72px", height: "calc(100% + 144px)", width: "100%" }}
        />
      </div>

      {/* Solid dark curtain when paused — hides the center YouTube "Watch on YouTube" branding */}
      {!playing && (
        <div
          className="pointer-events-none absolute inset-0 bg-[#070709]/85"
          aria-hidden
        />
      )}

      {/* Branded fallback when YouTube blocks embedded playback (private/embed-restricted/etc). */}
      {errorCode !== null && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center px-6 bg-[#070709]"
          data-testid="yt-player-error"
        >
          <div className="max-w-[440px] text-center">
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#E05252]">
              REPLAY TEMPORARILY UNAVAILABLE
            </div>
            <div
              className="mt-4 font-display"
              style={{
                fontSize: "clamp(20px, 2.2vw, 28px)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              We can&rsquo;t play this video right now.
            </div>
            <p className="mt-3 font-body text-[13px] leading-[1.7] text-[#F0ECE4]/60">
              The webinar is being finalized. Book a strategy call below and
              we&rsquo;ll walk you through everything from the training live.
            </p>
            <a
              href="#book"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="wam-btn-primary mt-6 inline-flex text-[11px]"
              data-testid="yt-error-cta"
            >
              Book A Strategy Call →
            </a>
          </div>
        </div>
      )}

      {/* Click-catcher covering the whole video — toggles play/pause */}
      <button
        type="button"
        aria-label={playing ? "Pause video" : "Play video"}
        onClick={togglePlay}
        onDoubleClick={toggleFs}
        className="absolute inset-0 h-full w-full cursor-pointer"
        style={{ background: "transparent" }}
        data-testid="yt-player-click"
      />

      {/* Center play glyph (only when paused) */}
      {!playing && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex h-[76px] w-[76px] md:h-[96px] md:w-[96px] items-center justify-center rounded-full bg-[#C9A84C] shadow-[0_20px_80px_-10px_rgba(201,168,76,0.55)]">
            <svg viewBox="0 0 24 24" width="30" height="30" className="translate-x-[3px]" fill="#070709" aria-hidden>
              <path d="M6 4l14 8-14 8V4z" />
            </svg>
          </div>
        </div>
      )}

      {/* Custom control bar */}
      <div
        className={`absolute inset-x-0 bottom-0 z-10 transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{
          background:
            "linear-gradient(to top, rgba(7,7,9,0.94) 0%, rgba(7,7,9,0.6) 55%, transparent 100%)",
        }}
      >
        {/* Interactive scrubber — click or drag to seek anywhere */}
        <div
          ref={barRef}
          role="slider"
          aria-label="Seek video"
          aria-valuemin={0}
          aria-valuemax={dur || 0}
          aria-valuenow={cur || 0}
          tabIndex={0}
          onPointerDown={onBarPointerDown}
          onPointerMove={onBarHover}
          onPointerLeave={() => !scrubbing && setHoverPct(null)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") { e.preventDefault(); skip(5); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); skip(-5); }
            else if (e.key === "Home") { e.preventDefault(); seekToPct(0); }
            else if (e.key === "End") { e.preventDefault(); seekToPct(0.999); }
          }}
          className="group/bar mx-3 md:mx-4 relative cursor-pointer select-none py-3 -my-2 touch-none"
          data-testid="yt-seek-bar"
        >
          <div className="relative h-[3px] group-hover/bar:h-[6px] transition-[height] duration-150 bg-white/15 overflow-visible">
            {/* Hover ghost fill */}
            {hoverPct !== null && (
              <div
                className="absolute inset-y-0 left-0 bg-white/25 pointer-events-none"
                style={{ width: `${hoverPct * 100}%` }}
              />
            )}
            {/* Actual progress */}
            <div
              className="absolute inset-y-0 left-0 bg-[#C9A84C] pointer-events-none"
              style={{
                width: `${pct}%`,
                transition: scrubbing ? "none" : "width 100ms linear",
              }}
            />
            {/* Scrub handle */}
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 md:h-3.5 md:w-3.5 rounded-full bg-[#C9A84C] shadow-[0_0_0_3px_rgba(7,7,9,0.9)] opacity-0 group-hover/bar:opacity-100 transition-opacity pointer-events-none"
              style={{
                left: `${pct}%`,
                opacity: scrubbing ? 1 : undefined,
              }}
              data-testid="yt-seek-handle"
            />
            {/* Time tooltip on hover */}
            {hoverPct !== null && dur > 0 && (
              <div
                className="absolute bottom-full mb-2 -translate-x-1/2 px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-[#F0ECE4] bg-[#070709]/95 border border-[#C9A84C]/30 pointer-events-none whitespace-nowrap"
                style={{ left: `${hoverPct * 100}%` }}
                data-testid="yt-seek-tooltip"
              >
                {fmt(hoverPct * dur)}
              </div>
            )}
          </div>
        </div>

        <div className="pointer-events-auto flex items-center gap-3 md:gap-4 px-3 md:px-4 py-3 text-[#F0ECE4]">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#C9A84C] hover:text-[#F0ECE4] transition-colors"
            data-testid="yt-play-toggle"
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                <path d="M6 4l14 8-14 8V4z" />
              </svg>
            )}
          </button>

          {/* Volume group */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              className="text-[#F0ECE4]/85 hover:text-[#C9A84C] transition-colors"
              data-testid="yt-mute-toggle"
            >
              {muted || volume === 0 ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                  <path d="M11 5l-4 4H4v6h3l4 4V5zM19 12l3 3-1.5 1.5L17.5 13.5 14.5 16.5 13 15l3-3-3-3 1.5-1.5L17.5 10.5 20.5 7.5 22 9l-3 3z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                  <path d="M11 5l-4 4H4v6h3l4 4V5zM16 8.5a4.5 4.5 0 010 7 1 1 0 11-1-1.72 2.5 2.5 0 000-3.56 1 1 0 011-1.72zM18 5.5a8 8 0 010 13 1 1 0 11-1-1.72 6 6 0 000-9.56 1 1 0 011-1.72z" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={muted ? 0 : volume}
              onChange={(e) => changeVolume(Number(e.target.value))}
              aria-label="Volume"
              className="wam-vol-slider h-1 w-16 md:w-24 accent-[#C9A84C]"
              data-testid="yt-volume"
              style={{ accentColor: "#C9A84C" }}
            />
          </div>

          {/* Time */}
          <div className="font-mono text-[10px] tracking-[0.12em] text-[#F0ECE4]/70 tabular-mono select-none">
            {fmt(cur)} <span className="text-[#F0ECE4]/30">/</span> {fmt(dur)}
          </div>

          <div className="flex-1" />

          {/* Speed */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSpeed((v) => !v)}
              aria-label="Playback speed"
              className="font-mono text-[11px] tracking-[0.18em] text-[#F0ECE4]/85 hover:text-[#C9A84C] transition-colors"
              data-testid="yt-speed-toggle"
            >
              {speed}×
            </button>
            {showSpeed && (
              <div
                className="absolute bottom-full right-0 mb-2 w-24 border border-[#C9A84C]/30 bg-[#070709]/95 backdrop-blur"
                data-testid="yt-speed-menu"
              >
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => changeSpeed(s)}
                    className={`block w-full px-3 py-2 text-left font-mono text-[10px] tracking-[0.18em] transition-colors ${
                      speed === s
                        ? "text-[#C9A84C] bg-[#C9A84C]/10"
                        : "text-[#F0ECE4]/70 hover:text-[#F0ECE4] hover:bg-white/5"
                    }`}
                  >
                    {s}×{speed === s ? " ●" : ""}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fullscreen */}
          <button
            type="button"
            onClick={toggleFs}
            aria-label={fs ? "Exit fullscreen" : "Fullscreen"}
            className="text-[#F0ECE4]/85 hover:text-[#C9A84C] transition-colors"
            data-testid="yt-fullscreen-toggle"
          >
            {fs ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                <path d="M9 4h2v6H5V8h4V4zM19 8h-4v2h6V4h-2v4zM9 20v-4H5v-2h6v6H9zM19 16v4h-2v-4h4v-2h-4v2z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                <path d="M5 5h6v2H7v4H5V5zm14 0v6h-2V7h-4V5h6zM5 19v-6h2v4h4v2H5zm14 0h-6v-2h4v-4h2v6z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
